import React, { useState, useEffect } from 'react';
import { ButcherHeader } from '@components/ButcherHeader';
import { SideNav } from '@components/OrderCreation/OrderSideNav';
import { Notification, AppNotification } from '@helpers/Models';
import { NotificationService } from '@services/NotificationService';
import { NotificationManager } from '@services/NotificationManager';
import Toast from '@components/Toast';
import Pagination from '@components/Pagination';
import { pagination } from '@helpers/Constants';
import { io, Socket } from 'socket.io-client';
import { SocketConfig } from '@helpers/Constants/Navigation';
import { Assets } from '@helpers/Constants/Assets';

export const AllNotifications: React.FC = () => {
  const [notifications, setNotifications] = useState<
    (Notification & { isReadByUser?: boolean })[]
  >([]);
  const [isMarkingAllAsRead, setIsMarkingAllAsRead] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(pagination.itemsPerPage);
  const [totalPages, setTotalPages] = useState(1);
  const [toast, setToast] = useState<{
    show: boolean;
    type: 'success' | 'error';
    title: string;
    message: string;
  }>({
    show: false,
    type: 'success',
    title: '',
    message: '',
  });

  // Load notifications using API on component mount
  useEffect(() => {
    const fetchNotifications = async () => {
      try {
        setIsLoading(true);

        // Get user details from localStorage
        const userDetailsStr = localStorage.getItem('userDetails');
        if (!userDetailsStr) {
          showToast(
            'error',
            'Error',
            'User details not found. Please login again.',
          );
          return;
        }

        const userDetails = JSON.parse(userDetailsStr);
        const { roles } = userDetails;
        // Fetch notifications using the new API with pagination
        const result = await NotificationService.fetchUserNotifications(
          roles,
          currentPage,
          itemsPerPage,
        );

        if (result.success && result.data) {
          setNotifications(result.data.notifications);
          setTotalPages(result.data.totalPages);
        } else {
          showToast(
            'error',
            'Error',
            result.message || 'Failed to fetch notifications',
          );
        }
      } catch (error) {
        console.error('Error fetching notifications:', error);
        showToast(
          'error',
          'Error',
          'Failed to fetch notifications. Please try again.',
        );
      } finally {
        setIsLoading(false);
      }
    };

    fetchNotifications();
  }, [currentPage, itemsPerPage]);

  // Set up socket listener for real-time notification updates
  useEffect(() => {
    const userInfo = localStorage.getItem('user');
    if (!userInfo) return;

    const user = JSON.parse(userInfo);
    const { role, butcherId, id: userId } = user;

    // Create socket connection
    const socket: Socket = io(SocketConfig.URL, {
      ...SocketConfig.OPTIONS,
      query: {
        role: role,
        userId: userId,
        butcherId: butcherId,
      },
    });

    // Listen for new notifications
    socket.on('new-notification', (data: AppNotification) => {
      console.log('Received new notification on AllNotifications page:', data);

      // Refetch notifications to update the list with the new notification
      // This ensures pagination and proper ordering are maintained
      const refetchNotifications = async () => {
        try {
          const userInfo = localStorage.getItem('user');
          if (!userInfo) return;

          const user = JSON.parse(userInfo);
          const roles = user.role ? [user.role] : [];

          const result = await NotificationService.fetchUserNotifications(
            roles,
            currentPage,
            itemsPerPage,
          );

          if (result.success && result.data) {
            setNotifications(result.data.notifications);
            setTotalPages(result.data.totalPages);
          }
        } catch (error) {
          console.error('Error refetching notifications:', error);
        }
      };

      refetchNotifications();
    });

    socket.on('connect', () => {
      console.log('Socket connected on AllNotifications page');
    });

    socket.on('connect_error', (error) => {
      console.error('Socket connection error on AllNotifications page:', error);
    });

    // Cleanup function
    return () => {
      socket.disconnect();
    };
  }, [currentPage, itemsPerPage]); // Dependencies to refetch when page changes

  // Function to show toast message
  const showToast = (
    type: 'success' | 'error',
    title: string,
    message: string,
  ) => {
    setToast({
      show: true,
      type,
      title,
      message,
    });
  };

  // Function to handle marking all notifications as read
  const handleMarkAllAsRead = async () => {
    if (notifications.length === 0) {
      showToast(
        'error',
        'No Notifications',
        'No notifications available to mark as read.',
      );
      return;
    }

    setIsMarkingAllAsRead(true);

    try {
      // Get only unread notification IDs from localStorage
      const unreadNotifications = NotificationManager.getUnreadNotifications();
      const unreadNotificationIds = unreadNotifications.map(
        (notification) => notification.id,
      );

      if (unreadNotificationIds.length === 0) {
        showToast(
          'success',
          'Already Read',
          'All notifications are already marked as read.',
        );
        setIsMarkingAllAsRead(false);
        return;
      }

      // Call the API to mark only unread notifications as read
      const result = await NotificationService.markAllAsRead(
        unreadNotificationIds,
      );

      // Reload notifications to reflect the changes first
      await reloadNotifications();

      // Clear unread notifications list after we have fresh data
      localStorage.setItem('unreadNotifications', JSON.stringify([]));

      // Dispatch custom event to notify other components of localStorage change
      // This is after both localStorage updates to ensure fresh data
      window.dispatchEvent(
        new CustomEvent('notificationsUpdated', {
          detail: {
            unreadCount: 0,
            notifications: JSON.parse(
              localStorage.getItem('notifications') || '[]',
            ),
            unreadNotifications: [],
          },
        }),
      );

      // Show success message
      showToast(
        'success',
        'Success',
        `${result.updatedCount} notification(s) marked as read successfully.`,
      );
    } catch (error: unknown) {
      // Show error message
      console.error('Error marking all notifications as read:', error);
      showToast(
        'error',
        'Error',
        'Failed to mark notifications as read. Please try again.',
      );
    } finally {
      setIsMarkingAllAsRead(false);
    }
  };

  // Function to reload notifications
  const reloadNotifications = async () => {
    try {
      const userDetailsStr = localStorage.getItem('userDetails');
      if (!userDetailsStr) return;

      const userDetails = JSON.parse(userDetailsStr);
      const { roles } = userDetails;

      if (!roles || !Array.isArray(roles) || roles.length === 0) return;

      const result = await NotificationService.fetchUserNotifications(
        roles,
        currentPage,
        itemsPerPage,
      );

      if (result.success && result.data) {
        localStorage.setItem(
          'notifications',
          JSON.stringify(result.data.notifications),
        );
        setNotifications(result.data.notifications);
        setTotalPages(result.data.totalPages);
      }
    } catch (error) {
      console.error('Error reloading notifications:', error);
    }
  };

  // Function to get icon based on action key
  const getNotificationIcon = (actionKey: string) => {
    const iconMap: { [key: string]: string } = {
      CUSTOMER_CREATE: Assets.CUSTOMER,
      ORDER_CREATE: Assets.ORDER_CREATE,
      SERVICE_START: Assets.SERVICE_PROCESSING,
      SERVICE_FINISH: Assets.SERVICE_PROCESSING,
      PRODUCT_START: Assets.PRODUCT_PROCESSING,
      PRODUCT_FINISH: Assets.PRODUCT_PROCESSING,
      GENERATE_INVOICE: Assets.INVOICE,
      PAID: Assets.INVOICE,
      PICKED_UP: Assets.PICKUP,
    };

    return iconMap[actionKey] || Assets.SERVICE_PROCESSING;
  };

  // Function to format date
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInMs = now.getTime() - date.getTime();
    const diffInDays = Math.floor(diffInMs / (1000 * 60 * 60 * 24));

    if (diffInDays === 0) {
      return 'Today';
    } else if (diffInDays === 1) {
      return '1 day ago';
    } else {
      return `${diffInDays} days ago`;
    }
  };

  return (
    <div className="flex h-screen w-screen gap-1">
      {/* Sidebar */}
      <SideNav active={3} />
      {/* Main Content */}
      <div className="flex-1 p-1 overflow-hidden relative flex flex-col">
        {/* Header */}

        <ButcherHeader />
        <div className="flex justify-between items-center p-3">
          <h1 className="font-semibold text-xl mt-3 text-[#1C1B1F]">
            All Notifications
          </h1>
          <button
            type="button"
            onClick={handleMarkAllAsRead}
            disabled={isMarkingAllAsRead || notifications.length === 0}
            className="text-primary font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isMarkingAllAsRead ? 'Marking...' : 'Mark all as read'}
          </button>
        </div>
        <div className="flex flex-col h-full">
          <div className="flex-1 px-3 overflow-auto space-y-4">
            {isLoading ? (
              <div className="flex justify-center items-center p-8 text-gray-500">
                <div className="text-center">
                  <div className="text-2xl mb-2">⏳</div>
                  <p>Loading notifications...</p>
                </div>
              </div>
            ) : notifications.length > 0 ? (
              notifications.map((notification) => (
                <div
                  key={notification.id}
                  className={`flex border border-[#FFFBEA] gap-x-4 p-2 ${
                    notification.isReadByUser === false
                      ? 'bg-[rgba(255,251,234,1)]'
                      : ''
                  }`}
                >
                  <div className="flex justify-center items-center">
                    <img
                      src={getNotificationIcon(notification.actionKey)}
                      alt="Notification"
                      className="w-10 h-10"
                    />
                  </div>
                  <div className="flex items-center w-9/12 px-2">
                    {notification.message}
                  </div>
                  <div className="text-[#A0A0A0] p-2">
                    {formatDate(notification.createdAt)}
                  </div>
                </div>
              ))
            ) : (
              <div className="flex justify-center items-center p-8 text-gray-500">
                <div className="text-center">
                  <div className="text-2xl mb-2">📢</div>
                  <p>No notifications available</p>
                </div>
              </div>
            )}
          </div>

          {notifications.length > 0 && (
            <div className="flex justify-center py-4  bg-white">
              <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                itemsPerPage={itemsPerPage}
                onPageChange={setCurrentPage}
                onItemsPerPageChange={setItemsPerPage}
              />
            </div>
          )}
        </div>
      </div>

      {/* Toast Component */}
      {toast.show && (
        <Toast
          type={toast.type}
          title={toast.title}
          message={toast.message}
          onClose={() =>
            setToast({ show: false, type: 'success', title: '', message: '' })
          }
        />
      )}
    </div>
  );
};
